import { useEffect, useMemo, useState } from "react";
import { getServices, getKbArticles } from "@/lib/apiClient";
import { Search, Package, BookOpen, ExternalLink, Copy, Check, Tag, Clock, Info } from "lucide-react";
import { toast } from "sonner";

const linkify = (text) =>
  (text || "").split(/(\s+)/).map((tok, i) =>
    /^https?:\/\//.test(tok)
      ? <a key={i} href={tok} target="_blank" rel="noreferrer" className="text-[#0C6E6E] underline break-all hover:text-[#16BFBF]">{tok}</a>
      : tok
  );

function CopyBtn({ value, label }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setDone(true); toast.success("Gekopieerd"); setTimeout(() => setDone(false), 1500); }
    catch { toast.error("Kopiëren mislukt"); }
  };
  return (
    <button data-testid="copy-link" onClick={copy} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-[#EAFBFB] text-[#0C6E6E] border border-[#D6F3F3] hover:bg-[#D6F3F3] transition-colors">
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {label}
    </button>
  );
}

function ServiceCard({ s, isAdminView }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`service-${s.id}`} className="rounded-2xl border border-[#E2E7EC] bg-white p-5 transition-all hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-semibold text-lg text-[#16202B]">{s.name}</h3>
            {s.category && <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-[#E3E9F5] text-[#001C51]">{s.category}</span>}
            {isAdminView && s.status !== "published" && <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-[#F3DEDD] text-[#B0413E]">{s.status}</span>}
          </div>
          {s.description && <p className="text-sm text-[#5B6675] mt-1.5">{s.description}</p>}
        </div>
        {s.price && <div className="text-right shrink-0"><p className="font-heading font-bold text-[#001C51] text-lg">{s.price}</p>{s.vat_status && <p className="text-xs text-[#5B6675]">{s.vat_status}</p>}</div>}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {s.duration && <span className="inline-flex items-center gap-1 text-xs text-[#5B6675]"><Clock className="h-3.5 w-3.5" /> {s.duration}</span>}
        {s.checkout_link && <CopyBtn value={s.checkout_link} label="Kopieer bestellink" />}
        {s.checkout_link && <a href={s.checkout_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-[#001C51] text-white hover:bg-[#0A337A]"><ExternalLink className="h-3.5 w-3.5" /> Open</a>}
        {(s.included || s.excluded || s.conditions || s.website_pages || s.internal_notes || s.price_formula) && (
          <button onClick={() => setOpen((o) => !o)} className="ml-auto text-xs font-medium text-[#001C51] hover:underline">{open ? "Minder" : "Meer details"}</button>
        )}
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-[#E2E7EC] grid sm:grid-cols-2 gap-4 text-sm">
          {s.price_formula && <Block label="Prijsformule">{s.price_formula}</Block>}
          {s.included && <Block label="Inbegrepen">{s.included}</Block>}
          {s.excluded && <Block label="Niet inbegrepen">{s.excluded}</Block>}
          {s.conditions && <Block label="Voorwaarden & bijzonderheden">{s.conditions}</Block>}
          {s.website_pages && <Block label="Relevante websitepagina's">{linkify(s.website_pages)}</Block>}
          {s.internal_notes && (
            <div className="sm:col-span-2 rounded-xl bg-[#FBF7EF] border border-[#F0E6D2] p-3">
              <p className="text-xs uppercase tracking-wide text-[#B7791F] font-semibold mb-1 flex items-center gap-1"><Info className="h-3.5 w-3.5" /> Interne instructies (medewerkers)</p>
              <p className="text-sm text-[#16202B] whitespace-pre-wrap">{s.internal_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Block = ({ label, children }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-[#5B6675] font-semibold mb-1">{label}</p>
    <div className="text-[#16202B] whitespace-pre-wrap">{children}</div>
  </div>
);

function ArticleCard({ a }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`kb-${a.id}`} className="rounded-2xl border border-[#E2E7EC] bg-white p-5">
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading font-semibold text-[#16202B]">{a.title}</h3>
          {a.category && <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-[#E3E9F5] text-[#001C51]">{a.category}</span>}
        </div>
      </button>
      {open && <div className="mt-3 pt-3 border-t border-[#E2E7EC] text-sm text-[#16202B] whitespace-pre-wrap leading-relaxed">{linkify(a.body)}</div>}
      {a.tags && <div className="flex flex-wrap gap-1.5 mt-3">{a.tags.split(",").map((t, i) => t.trim() && <span key={i} className="inline-flex items-center gap-1 text-xs text-[#5B6675] bg-[#EDF1F5] rounded-full px-2 py-0.5"><Tag className="h-3 w-3" />{t.trim()}</span>)}</div>}
    </div>
  );
}

export default function Kennisbank() {
  const [tab, setTab] = useState("services");
  const [services, setServices] = useState([]);
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    getServices().then(setServices).catch(() => {});
    getKbArticles().then(setArticles).catch(() => {});
  }, []);

  const term = q.toLowerCase().trim();
  const fServices = useMemo(() => !term ? services : services.filter((s) =>
    [s.name, s.category, s.description, s.included, s.excluded, s.conditions, s.internal_notes].join(" ").toLowerCase().includes(term)), [services, term]);
  const fArticles = useMemo(() => !term ? articles : articles.filter((a) =>
    [a.title, a.category, a.body, a.tags].join(" ").toLowerCase().includes(term)), [articles, term]);

  return (
    <div className="px-6 lg:px-12 py-10 max-w-5xl mx-auto fe-fade-up">
      <header className="mb-6">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#16202B]">Diensten & kennisbank</h1>
        <p className="text-[#5B6675] mt-2">Pakketten, prijzen, voorwaarden en interne kennis — altijd bij de hand tijdens het bellen.</p>
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="inline-flex rounded-xl border border-[#E2E7EC] bg-white p-1">
          <button data-testid="kb-tab-services" onClick={() => setTab("services")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === "services" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><Package className="h-4 w-4" /> Diensten & pakketten</button>
          <button data-testid="kb-tab-articles" onClick={() => setTab("articles")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === "articles" ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}><BookOpen className="h-4 w-4" /> Kennisbank</button>
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8994A3]" />
          <input data-testid="kb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam, prijs, voorwaarde…" className="fe-input pl-10" />
        </div>
      </div>

      {tab === "services" ? (
        fServices.length === 0 ? <Empty icon={Package} text="Nog geen gepubliceerde diensten. De beheerder voegt ze toe onder Beheer → Diensten." />
          : <div className="space-y-4" data-testid="services-list">{fServices.map((s) => <ServiceCard key={s.id} s={s} isAdminView={services.some((x) => x.status !== "published")} />)}</div>
      ) : (
        fArticles.length === 0 ? <Empty icon={BookOpen} text="Nog geen gepubliceerde artikelen. De beheerder voegt ze toe onder Beheer → Kennisbank." />
          : <div className="space-y-3" data-testid="articles-list">{fArticles.map((a) => <ArticleCard key={a.id} a={a} />)}</div>
      )}
    </div>
  );
}

const Empty = ({ icon: Icon, text }) => (
  <div className="rounded-2xl border border-dashed border-[#E2E7EC] bg-white p-16 text-center" data-testid="kb-empty">
    <div className="h-14 w-14 rounded-2xl bg-[#EDF1F5] flex items-center justify-center mx-auto mb-4"><Icon className="h-6 w-6 text-[#001C51]" strokeWidth={1.5} /></div>
    <p className="text-[#5B6675] max-w-sm mx-auto">{text}</p>
  </div>
);
